<script setup lang="ts">
const { showGameListTitles } = useLinks();
const { showMoneyFormat } = useWallet();
const { isMobile } = useLayout();
const { openPreview } = useGameMobilePreview();
const { isProvidersHomePage, normalizeThumbnail } = useCasino();
const { getRibbonIcon } = useRibbon();
const { getResponsiveThumbnailSrc } = useResponsiveThumbnail();
const { getLoadingStrategy } = useImageLoading();
const router = useRouter();

const props = withDefaults(
  defineProps<{
    game: Game;
    type?: MediaType;
    index?: number;
    loading?: 'lazy' | 'eager';
    showPayToday?: boolean;
    noLink?: boolean;
    skeleton?: boolean;
    top10?: boolean;
  }>(),
  {
    type: 'game',
    index: 0,
    loading: 'lazy',
    showPayToday: false,
    noLink: false,
    skeleton: false,
    top10: false,
  },
);

const isProviderType = computed(() => props.type === 'provider');

const formattedPayToday = computed(() => {
  if (!props.showPayToday) return '';

  const value = Number(props.game.turnover) || 0;

  return showMoneyFormat({ value });
});

const isGamesCountBadgeVisible = computed(
  () => isProvidersHomePage.value && isProviderType.value,
);

const subtitle = computed(() => props.game?.provider?.name || '');

const ribbonIcon = computed(() =>
  props.game.ribbon ? getRibbonIcon(props.game.ribbon) : null,
);

function hexToRgb(hex: string): [number, number, number] | null {
  const clean = hex.replace('#', '');

  if (clean.length !== 6) return null;

  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

function darkenHex(hex: string, amount = 60): string {
  const rgb = hexToRgb(hex);

  if (!rgb) return hex;

  const [r = 0, g = 0, b = 0] = rgb.map((v) => Math.max(0, v - amount));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const ribbonBadgeStyle = computed(() => {
  const color = props.game.ribbon?.color ?? '#ff4400';
  const dark = darkenHex(color);

  return {
    background: `linear-gradient(to bottom, ${color}, ${dark})`,
    color: props.game.ribbon?.text_color ?? '#ffffff',
  };
});

const linkTo = computed(() => {
  const slug = props.game.slug ? props.game.slug.toLowerCase() : '';

  return isProviderType.value ? `/games/providers/${slug}` : `/games/${slug}`;
});

function isCloudfrontImage(path: string): boolean {
  return path.includes('cloudfront.net');
}

const isVideo = computed(
  () =>
    props.game.media_type === 'video' ||
    /\.mp4($|\?)/i.test(props.game.image ?? ''),
);

const thumbnailSrc = computed(() => {
  const src = props.game.image || '';

  if (!src) return '';

  // Vídeos não passam por transformações de imagem (CDN/responsive
  // thumbnail) — esses helpers reescrevem a URL pra parâmetros que só
  // fazem sentido pra imagem estática.
  if (isVideo.value) return src;

  if (props.game.useCdnOnMobile === false) return getAssetsUrlForCDN(src);

  if (isProviderType.value) return normalizeThumbnail(src, true);

  if (isCloudfrontImage(src)) return normalizeThumbnail(src);

  return getResponsiveThumbnailSrc(src);
});

// Sem poster o card fica preto até o primeiro frame ser decodificado. O campo
// é opcional: enquanto o backend não mandar, posterSrc fica undefined e quem
// cobre a lacuna é o shimmer (videoReady).
//
// Variante própria, sem passar pelo getResponsiveThumbnailSrc. Dois motivos:
//
// 1. Só largura, sem `height` nem `fit=cover`. A capa é o frame 0 do vídeo,
//    então já nasce na proporção dele; pedir a variante 320x373 dos thumbs
//    estáticos (6:7) recortaria a capa numa proporção diferente da do vídeo, e
//    a troca de uma pela outra apareceria como um salto de zoom. Preservando a
//    proporção, o object-fit: cover do CSS trata as duas exatamente igual.
// 2. A URL precisa de variante: o poster é imagem nossa no Cloudflare Images, e
//    o Cloudflare responde 400 quando a URL vem sem nenhuma.
const posterSrc = computed(() => {
  const src = props.game.image_poster;

  if (!isVideo.value || !src) return undefined;

  return `${normalizeThumbnail(src)}/width=320,quality=70,format=auto`;
});

// NÃO adiar o `src` do vídeo. Já tentamos segurá-lo até a capa pintar, para o
// mp4 não disputar banda com ela, e o preço foi alto: no iOS o autoplay só é
// concedido quando o elemento nasce elegível, e um `src` atribuído depois da
// criação faz o Safari bloquear e desenhar o botão de play nativo. O ganho de
// banda também não se confirmou — o mp4 baixa em ~400ms, não é ele que atrasa
// a capa.

// `loadeddata` = primeiro frame disponível, ou seja, o <video> já tem o que
// pintar e o poster pode sair.
const videoReady = ref(false);

// Falha é estado separado de propósito: ela libera o shimmer (que senão giraria
// pra sempre), mas NÃO o poster — num vídeo que não carrega, a capa passa a ser
// a única coisa visível no card.
const videoFailed = ref(false);

function onVideoLoadedData() {
  videoReady.value = true;
}

function onVideoError() {
  videoFailed.value = true;
}

const loadingAttr = computed(
  () => props.loading ?? getLoadingStrategy(props.index, 0),
);

// isClientMounted evita hydration mismatch na RENDERIZAÇÃO: SSR produz <a>
// (isMobile=false no servidor) e queremos que o primeiro render do client
// case com isso. Só depois do onMounted trocamos pra <div> no mobile.
const isClientMounted = ref(false);

const videoEl = ref<HTMLVideoElement | null>(null);
let videoObserver: IntersectionObserver | null = null;

onMounted(() => {
  isClientMounted.value = true;

  // Listagens grandes (Slots/Live) ficam pesadíssimas se todos os <video>
  // tocam ao mesmo tempo. Pausa quando sai do viewport.
  if (isVideo.value && videoEl.value && 'IntersectionObserver' in window) {
    videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLVideoElement;

          if (entry.isIntersecting) {
            el.play().catch(() => {
              /* autoplay bloqueado: ignora */
            });
          } else {
            el.pause();
          }
        }
      },
      { threshold: 0.25 },
    );
    videoObserver.observe(videoEl.value);
  }
});

onBeforeUnmount(() => {
  videoObserver?.disconnect();
  videoObserver = null;
});

// iOS Safari: sem o primeiro frame decodificado, o vídeo aparece preto e às
// vezes o overlay nativo de "play" sobrepõe. Setar currentTime força o
// decoder a renderizar um frame imediatamente, e o muted defensivo evita
// que o autoplay seja bloqueado por race condition de hydration.
//
// O play() vem ANTES do seek de propósito: no iOS o seek pendente segura o
// play (é o mesmo problema de range request do CDN descrito no onVideoEnded),
// e se o vídeo está tocando o frame já aparece — o nudge do currentTime só é
// necessário quando o autoplay foi mesmo recusado.
function onVideoLoaded(event: Event) {
  const el = event.target as HTMLVideoElement;

  el.muted = true;

  el.play().catch(() => {
    /* autoplay bloqueado (ex.: Low Power Mode): mostra ao menos um frame */
    if (el.currentTime === 0) {
      try {
        el.currentTime = 0.001;
      } catch {
        /* ignora se readyState ainda não permite seek */
      }
    }
  });
}

// Loop manual em vez do atributo nativo `loop`: com `loop` presente o
// browser tenta dar seek pro início sozinho e NÃO dispara `ended` — e esse
// seek nativo falha aqui (CDN sem range request + preload="metadata"),
// congelando no último frame. Sem `loop`, o `ended` dispara e reiniciamos
// na mão, o que rebufferiza desde o começo. Funciona igual no desktop e iOS.
function onVideoEnded(event: Event) {
  const el = event.target as HTMLVideoElement;

  el.currentTime = 0;

  el.play().catch(() => {
    /* autoplay bloqueado (ex.: Low Power Mode): ignora */
  });
}

// Decisão de RENDERING (tag type). Usa isClientMounted pra evitar mismatch.
const shouldRenderAsLink = computed(() => {
  if (props.noLink) return false;
  if (!isClientMounted.value) return true; // SSR/pré-mount: sempre <a>

  return !isMobile.value || isProviderType.value;
});

// Decisão de CLICK BEHAVIOR. NÃO depende de isClientMounted — no client,
// isMobile reflete a viewport real imediatamente, então mesmo se o root
// ainda estiver como <a> por alguns ms antes do onMounted, o tap é
// interceptado corretamente e abre o preview em vez de navegar.
const isMobileGameClickable = computed(
  () => !props.noLink && isMobile.value && !isProviderType.value,
);

// Usado pra classe CSS do cursor no mobile
const isMobileGameCard = isMobileGameClickable;

// Navegação "clique simples" reutilizável: mobile abre o preview, desktop faz
// push SPA. Exposta via defineExpose para o botão JOGAR do top10, que vive
// fora do <a> (por causa do overflow do card) e precisa disparar o mesmo
// comportamento do clique no card.
function navigate() {
  if (props.noLink) return;

  if (isMobileGameClickable.value) {
    openPreview(props.game);

    return;
  }

  router.push(linkTo.value);
}

function handleClick(e: MouseEvent) {
  if (props.noLink) return;

  // Desktop: navegação SPA manual — só quando o clique é "simples".
  // Modificadores (ctrl/cmd/shift/middle-click) deixam o navegador lidar
  // (abrir em nova aba etc.), preservando o comportamento do <a href>.
  // No mobile (preview) não há href, então não há modificador a respeitar.
  if (
    !isMobileGameClickable.value &&
    (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
  ) {
    return;
  }
  e.preventDefault();
  navigate();
}

defineExpose({ navigate });
</script>

<template>
  <div :class="$style.cardRoot">
    <component
      :is="shouldRenderAsLink ? 'a' : 'div'"
      v-bind="shouldRenderAsLink && !skeleton ? { href: linkTo } : {}"
      :class="[
        $style.card,
        {
          [$style.providerCard]: isProviderType,
          [$style.noLink]: noLink,
          [$style.mobileCard]: isMobileGameCard && !skeleton,
        },
      ]"
      draggable="false"
      @click="skeleton ? null : handleClick($event)"
      @dragstart.prevent
    >
      <!-- Imagem com overflow:hidden para cortar bordas sem cortar o ribbon -->
      <div
        :class="[
          $style.imageWrapper,
          {
            [$style.providerImageWrapper]: isProviderType,
            [$style.fillImageWrapper]: noLink,
            [$style.top10ImageWrapper]: props.top10,
          },
        ]"
      >
        <video
          v-if="isVideo"
          ref="videoEl"
          v-autoplay-muted
          :aria-label="game.name"
          autoplay
          :class="$style.image"
          disablepictureinpicture
          disableremoteplayback
          muted
          playsinline
          preload="metadata"
          :src="thumbnailSrc"
          webkit-playsinline
          @ended="onVideoEnded"
          @error="onVideoError"
          @loadeddata="onVideoLoadedData"
          @loadedmetadata="onVideoLoaded"
        ></video>
        <img
          v-else
          :alt="game.name"
          :class="$style.image"
          :draggable="false"
          :fetchpriority="loadingAttr === 'eager' ? 'high' : undefined"
          :loading="loadingAttr"
          :src="thumbnailSrc"
        />
        <!-- Poster como <img> e não como atributo `poster` do <video>: o
             atributo é buscado em prioridade baixa e ainda concorre com o
             preload do vídeo, então chegava depois dos thumbs vizinhos. Como
             <img> ele entra na mesma leva, com o mesmo loading/fetchpriority.
             Sai quando o vídeo tem frame (`loadeddata`), então não há flash.

             Este bloco e o shimmer precisam vir DEPOIS do <img v-else>: entre
             o v-if e o v-else eles roubariam a cadeia e o <img> passaria a
             renderizar junto do vídeo, com a URL do mp4 no src (imagem
             quebrada + alt do jogo). -->
        <img
          v-if="isVideo && posterSrc && !videoReady"
          :alt="game.name"
          :class="$style.image"
          :draggable="false"
          :fetchpriority="loadingAttr === 'eager' ? 'high' : undefined"
          :loading="loadingAttr"
          :src="posterSrc"
        />
        <!-- Sem poster, cobre o preto do bg-bg-primary até o vídeo ter frame -->
        <div
          v-if="isVideo && !posterSrc && !videoReady && !videoFailed"
          :class="[$style.skeletonFill, 'skeleton-loading']"
        />
        <span
          v-if="isGamesCountBadgeVisible"
          :class="$style.badge"
        >
          {{ game.games }}
        </span>

        <!-- Ribbon centralizado no card -->
        <div
          v-if="game.ribbon?.name && !isProviderType"
          :class="$style.ribbonWrap"
        >
          <div
            :class="$style.ribbonBadge"
            :style="ribbonBadgeStyle"
          >
            <span
              v-if="ribbonIcon?.url"
              aria-hidden="true"
              :class="[$style.ribbonBarIcon, $style.ribbonBarIconMask]"
              :style="{
                maskImage: `url(${ribbonIcon.url})`,
                WebkitMaskImage: `url(${ribbonIcon.url})`,
              }"
            />
            <NuxtIcon
              v-else-if="ribbonIcon?.name"
              :class="$style.ribbonBarIcon"
              filled
              :name="ribbonIcon.name ?? ''"
            />
            <span :class="$style.ribbonBarText">{{ game.ribbon.name }}</span>
          </div>
        </div>

        <!-- Skeleton overlay: cobre o imageWrapper sem trocar a estrutura -->
        <div
          v-if="skeleton"
          :class="[$style.skeletonFill, 'skeleton-loading']"
        />
      </div>

      <!-- Descrição (título + provider) quando showGameListTitles estiver ativo -->
      <template v-if="!isProviderType">
        <div
          :class="[$style.description, { [$style.show]: showGameListTitles }]"
        >
          <span :class="$style.title">{{ game.name }}</span>
          <span :class="$style.subtitle">{{ subtitle }}</span>
        </div>

        <section :class="$style.overlay">
          <span :class="$style.playButton">
            <NuxtIcon name="play" />
            <span>{{ $t('casino.game.play') }}</span>
          </span>
        </section>
      </template>

      <section
        v-else
        :aria-label="game.name"
        :class="$style.overlay"
      />
    </component>

    <!-- Pay Today badge: balão azul (NuxtIcon bg) — fica como div irmã do
         card pra não ser cortado pelo overflow-hidden/rounded do card. -->
    <div
      v-if="showPayToday || formattedPayToday"
      :class="$style.payTodayBadge"
    >
      <NuxtIcon
        :class="$style.payTodayBg"
        filled
        name="filter/pay_today_bg"
      />
      <div
        v-if="!skeleton && formattedPayToday"
        :class="$style.payTodayContent"
      >
        <span :class="$style.payTodayLabel">Apostado Hoje</span>
        <div :class="$style.payTodayRow">
          <NuxtIcon
            :class="$style.payTodayCoin"
            filled
            name="filter/coin"
          />
          <span :class="$style.payTodayValue">{{ formattedPayToday }}</span>
        </div>
      </div>
      <div
        v-if="skeleton"
        :class="[$style.skeletonFill, 'skeleton-loading']"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
.cardRoot {
  @apply flex flex-col w-full max-w-[350px];
  gap: 4px;
}

/* No modo noLink (ex.: thumb do MobilePreview), o pai define altura via
   aspect-ratio. O imageWrapper interno usa h-full, então o cardRoot
   precisa propagar essa altura — sem isso a imagem some. */
.cardRoot:has(.noLink) {
  height: 100%;
  border-radius: inherit;
}

.card {
  @apply h-auto w-full relative flex flex-col overflow-hidden;
  border-radius: var(--card-border-radius, 12px);
  user-select: none;
  -webkit-user-drag: none;

  @media (max-width: 768px) {
    border-radius: var(--card-border-radius, 7.31px);
  }

  .overlay {
    @apply absolute top-0 flex justify-center items-center bg-game-overlay/80 z-40 w-full h-full invisible;

    .playButton {
      @apply bg-game-button-bg text-game-button-text font-bold text-[.7rem] xs:text-[.75rem] uppercase py-1.5 px-2.5 xs:py-2 xs:px-4 rounded-md flex-row flex gap-2 items-center;

      svg {
        @apply text-game-icon;
      }
    }
  }

  &.providerCard {
    @apply overflow-hidden aspect-[3/2];
  }

  &.noLink {
    max-width: none;
    aspect-ratio: unset;
    width: 100%;
    height: 100%;
  }

  &.mobileCard {
    cursor: pointer;
  }
}

.cardRoot:has(.noLink) .card {
  flex: 1 1 auto;
  height: 100%;
}

.top10ImageWrapper {
  aspect-ratio: 150 / 199.5;
  /* Sobrescreve pt-[116%] do .imageWrapper (hack de aspect-ratio por padding-top) */
  padding-top: 0 !important;
  mask-image: url('/assets/images/perfil-oficial/top10-card-mask.svg');
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  mask-mode: alpha;
  -webkit-mask-image: url('/assets/images/perfil-oficial/top10-card-mask.svg');
  -webkit-mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  /* Sobrescreve border-radius do .imageWrapper para preservar o clip do mask SVG */
  border-radius: 0 !important;
}

.imageWrapper {
  @apply inline-block w-full relative bg-bg-primary overflow-hidden shadow-md;
  padding-top: var(--card-image-aspect, 116%);
  container-type: inline-size;

  @media (max-width: 768px) {
    padding-top: var(--card-image-aspect, 128.74%);
  }

  .image {
    @apply absolute inset-0 object-cover w-full h-full;
  }

  .badge {
    @apply absolute top-0 right-0 z-40 m-2 py-0.5 px-1.5 bg-texts/40 text-bg-primary rounded font-semibold leading-none text-xs;
  }

  .ribbonWrap {
    @apply absolute top-0 left-0 w-full z-[35] flex justify-center;
  }

  .ribbonBadge {
    @apply flex items-center font-extrabold uppercase leading-none;
    border-radius: 0 0 4px 4px;
    gap: clamp(3.5px, 0.5px + 3.5cqw, 5px);
    padding: clamp(3px, 0.5px + 3cqw, 8px) clamp(5px, 1px + 5cqw, 14px);
    font-size: clamp(6px, 1.6px + 5.5cqw, 10px);
    letter-spacing: 0.06em;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);
  }

  .ribbonBarIcon {
    flex-shrink: 0;
    width: auto;
    max-height: 1em;
    object-fit: contain;

    svg {
      width: auto;
      height: 1em;
    }
  }

  .ribbonBarIconMask {
    display: inline-block;
    width: 1em;
    height: 1em;
    background-color: currentColor;
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    -webkit-mask-size: contain;
  }

  .ribbonBarText {
    @apply leading-none;
  }

  &.fillImageWrapper {
    @apply pt-0 w-full h-full;
  }

  &.providerImageWrapper {
    @apply pt-0 bg-bg-secondary w-full h-full;

    .image {
      @apply object-contain object-center max-w-full max-h-full p-3;

      @media (max-width: 1200px) {
        @apply p-2;
      }
    }
  }
}

.description {
  @apply absolute bottom-0 left-0 w-full flex flex-col px-2.5 pb-1.5 z-30 invisible opacity-0;

  &.show {
    @apply visible opacity-100;
  }

  .title {
    @apply text-game-title text-[.66rem] sm:text-[.7rem] lg:text-[.8rem] font-bold truncate max-w-full leading-3 sm:leading-4 lg:leading-5;
  }

  .subtitle {
    @apply text-game-subtitle/80 text-[.6rem] sm:text-[.64rem] lg:text-[.72rem] font-normal leading-tight truncate max-w-full;
  }
}

.payTodayBadge {
  @apply relative w-full overflow-hidden rounded-md;
}

.payTodayBg {
  @apply block w-full;

  svg {
    @apply block w-full h-full;
  }

  height: 40px;

  @media (min-width: 768px) {
    height: 44px;
  }

  @media (min-width: 1200px) {
    height: 48px;
  }
}

.payTodayContent {
  @apply absolute inset-0 flex flex-col items-center mx-1 justify-center gap-0.5;
  padding-left: 0;
  padding-right: 0;

  @media (min-width: 469px) {
    padding-left: 8px;
    padding-right: 8px;
  }
}

.payTodayLabel {
  @apply text-[8px] font-semibold leading-none pt-1;

  color: #00c9ff;

  @media (min-width: 768px) {
    font-size: 9px;
  }

  @media (min-width: 1200px) {
    font-size: 10px;
  }
}

.payTodayRow {
  @apply flex items-center -mx-0.5 justify-center gap-0.5;
}

.payTodayCoin {
  @apply text-[12px] flex-shrink-0;

  @media (min-width: 469px) {
    font-size: 16px;
  }

  @media (min-width: 1200px) {
    font-size: 18px;
  }
}

.payTodayValue {
  @apply text-[9px] font-bold text-white truncate;

  @media (min-width: 469px) {
    font-size: 11px;
  }
}

.skeletonFill {
  @apply absolute inset-0 z-30;
  border-radius: 6px;
}

.payTodaySkeleton {
  height: 40px;

  @media (min-width: 768px) {
    height: 44px;
  }

  @media (min-width: 1200px) {
    height: 48px;
  }
}
</style>
